import { Avatar } from '@blacknbrownstudios/ui';
import type { LoadingPhase, ActiveTool } from '../types';

interface StatusIndicatorProps {
  loadingPhase: LoadingPhase;
  activeTool: ActiveTool | null;
  pendingMessage: string;
}

export function StatusIndicator({
  loadingPhase,
  activeTool,
  pendingMessage,
}: StatusIndicatorProps) {
  if (loadingPhase === 'idle') {
    return null;
  }

  const getStatusMessage = () => {
    switch (loadingPhase) {
      case 'thinking':
        return 'Thinking...';
      case 'tool_calling':
        return activeTool ? `Using ${activeTool.name}...` : 'Calling tool...';
      case 'generating':
        return 'Writing response...';
      default:
        return '';
    }
  };

  const getPhaseColor = () => {
    switch (loadingPhase) {
      case 'thinking':
        return 'text-blue-400';
      case 'tool_calling':
        return 'text-amber-400';
      case 'generating':
        return 'text-green-400';
      default:
        return 'text-neutral-400';
    }
  };

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <Avatar size="small" className="bg-primary-600 flex-shrink-0">
        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-sm font-medium ${getPhaseColor()}`}>{getStatusMessage()}</span>
          <span className="flex gap-1">
            <span
              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
              style={{ animationDelay: '300ms' }}
            />
          </span>
        </div>

        {/* Show tool arguments when calling a tool */}
        {loadingPhase === 'tool_calling' && activeTool?.arguments && (
          <div className="text-xs text-neutral-500 bg-neutral-800/50 rounded px-2 py-1 font-mono overflow-x-auto">
            {Object.entries(activeTool.arguments)
              .slice(0, 3)
              .map(([key, value]) => (
                <div key={key} className="truncate">
                  <span className="text-neutral-400">{key}:</span>{' '}
                  <span className="text-neutral-300">
                    {typeof value === 'string'
                      ? value.slice(0, 50)
                      : JSON.stringify(value).slice(0, 50)}
                    {(typeof value === 'string' ? value.length : JSON.stringify(value).length) > 50
                      ? '...'
                      : ''}
                  </span>
                </div>
              ))}
            {Object.keys(activeTool.arguments).length > 3 && (
              <div className="text-neutral-500">
                +{Object.keys(activeTool.arguments).length - 3} more
              </div>
            )}
          </div>
        )}

        {/* Show streaming content preview when generating */}
        {loadingPhase === 'generating' && pendingMessage && (
          <div className="text-sm text-neutral-300 whitespace-pre-wrap">
            {pendingMessage}
            <span className="inline-block w-2 h-4 bg-neutral-400 ml-0.5 animate-pulse" />
          </div>
        )}
      </div>
    </div>
  );
}
